import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { setCurrentProgram, setLocale } from "../../store/features/globalSlice";
import { useLocale } from "../../hooks/useLocale";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";

import "./region-settings.scss";

export const LOCALES = [
  {
    locale: "en",
    countryISO2: "US",
    name: "English",
  },
  {
    locale: "ae",
    countryISO2: "AE",
    name: "Arabic",
  },
];

const getFlag = (iso2 = "US", size = 16) =>
  `https://flagsapi.com/${iso2}/flat/${size}.png`;

const RegionSettings = () => {
  const dispatch = useDispatch();
  const countryList = useSelector((state) => state.global.countryList);
  const currentProgram = useSelector((state) => state.global.currentProgram);

  const [showRegionSettingMenu, setShowRegionSettingMenu] = useState(false);
  const regionSettingMenuRef = useRef(null);

  const locale = useSelector((s) => s.global.locale);
  const { i18n } = useTranslation();

  useEffect(() => {
    dispatch(setCurrentProgram(countryList?.programs[0]?.programHashId));
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        regionSettingMenuRef.current &&
        !regionSettingMenuRef.current.contains(event.target)
      ) {
        setShowRegionSettingMenu(false);
      }
    }

    if (showRegionSettingMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showRegionSettingMenu]);

  const handleProgramSelect = (e) => {
    dispatch(setCurrentProgram(e.target.value));
  };

  const handleLangSelect = (locale) => {
    const lang = LOCALES.find((item) => item.locale === locale);
    if (!lang) return;

    // update locale on store
    dispatch(setLocale(lang));

    // change the locale on the UI
    i18n.changeLanguage(lang.locale);

    toast(`Switched To ${lang.name} Language`, {
      type: "success",
    });
  };

  if (!currentProgram || !countryList?.programs) {
    return <Skeleton width={100} className="mx-3" height={28} />;
  }

  const countryISO2 =
    countryList?.programs[0]?.country?.toLowerCase() ===
      "united arab emirates" && "AE";

  const handleRegionSettingMenu = () => {
    setShowRegionSettingMenu((prev) => !prev);
  };

  return (
    <div className="mx-3 position-relative">
      <div
        className="btn d-flex justify-content-between gap-2 bg-light p-2 rounded fw-medium region-setting-container"
        onClick={handleRegionSettingMenu}
      >
        <div>
          <img src={getFlag(countryISO2)} />
        </div>
        <span>UAE, </span>
        <span className="text-uppercase">{locale.locale}</span>
      </div>
      {showRegionSettingMenu && (
        <div
          className="region-setting-menu-container rounded"
          ref={regionSettingMenuRef}
        >
          <div className="d-flex flex-column gap-1 mb-3">
            <label htmlFor="program">Country</label>
            <select
              className="form-select"
              id="program"
              disabled
              onChange={(e) => handleProgramSelect(e)}
              value={currentProgram}
            >
              {countryList.programs.map((item) => {
                return (
                  <option value={item.programHashId}>{item.country}</option>
                );
              })}
            </select>
          </div>

          <div className="d-flex flex-column gap-1">
            <label htmlFor="localeSwitcher">Language</label>
            <select
              className="form-select text-capitalize"
              id="localeSwitcher"
              onChange={(e) => handleLangSelect(e.target.value)}
              value={locale.locale}
            >
              {LOCALES.map((item) => {
                return <Option key={item.locale} item={item} />;
              })}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

const Option = ({ item }) => {
  const { localize } = useLocale();
  return (
    <option value={item.locale}>
      {localize(`common.lang.${item.locale}`, { returnObjects: true })}
    </option>
  );
};

export default RegionSettings;
